// =============================================================================
// game-store.ts — Game state management (Zustand)
// Project: Texas Hold'em Social Casino
// =============================================================================
// Manages the WebSocket connection to the PokerTable DO and the local
// mirror of game state. Handles reconnection on network interruption.
//
// The store is reset when the player returns to the lobby.

import { create } from 'zustand'
import { GameState, ServerMessage, ClientMessage, ActionType, HandResult, ChatMessage } from '../types'

const WS_PING_INTERVAL = 15_000  // 15s keepalive ping
const WS_RECONNECT_DELAY = 2_000  // 2s before reconnect attempt

interface GameStore {
  // Connection
  ws: WebSocket | null
  isConnected: boolean
  tableId: string | null

  // Game state (mirror of server state)
  gameState: GameState | null
  isMyTurn: boolean
  availableActions: ActionType[]
  minRaise: number
  maxRaise: number
  myPlayerId: string | null
  lastHandResult: HandResult | null
  handAnimationActive: boolean

  // Chat
  messages: ChatMessage[]

  // Actions
  connectToTable: (tableId: string, playerId: string, playerName: string, token: string, tableSize?: number, stakeLevel?: string) => void
  disconnectFromTable: () => void
  sendAction: (action: ActionType, amount: number) => void
  sendChat: (text: string) => void
  sendEmote: (emoteId: string) => void
  sendPing: () => void
  clearHandResult: () => void
  reset: () => void
}

export const useGameStore = create<GameStore>((set, get) => ({
  ws: null,
  isConnected: false,
  tableId: null,
  gameState: null,
  isMyTurn: false,
  availableActions: [],
  minRaise: 0,
  maxRaise: 0,
  myPlayerId: null,
  lastHandResult: null,
  handAnimationActive: false,
  messages: [],

  connectToTable: (tableId, playerId, playerName, token, tableSize?, stakeLevel?) => {
    const wsUrl = `${import.meta.env.VITE_WS_URL || 'wss://poker-backend.sunny0angel.workers.dev'}/do/ws?tableId=${tableId}&tableSize=${tableSize || 6}&stakeLevel=${stakeLevel || 'micro'}&playerId=${playerId}&playerName=${encodeURIComponent(playerName)}&token=${token}`

    const ws = new WebSocket(wsUrl)

    ws.addEventListener('open', () => {
      set({ isConnected: true, tableId, myPlayerId: playerId })
    })

    ws.addEventListener('message', (event) => {
      const msg: ServerMessage = JSON.parse(event.data)
      handleServerMessage(msg, set, get)
    })

    ws.addEventListener('close', () => {
      set({ isConnected: false })
      // Attempt reconnection
      setTimeout(() => {
        const state = get()
        if (state.tableId) {
          state.connectToTable(tableId, playerId, playerName, token)
        }
      }, WS_RECONNECT_DELAY)
    })

    ws.addEventListener('error', () => {
      ws.close()
    })

    set({ ws })

    // Start ping interval
    const pingInterval = setInterval(() => {
      const state = get()
      if (state.ws?.readyState === WebSocket.OPEN) {
        state.sendPing()
      } else {
        clearInterval(pingInterval)
      }
    }, WS_PING_INTERVAL)
  },

  disconnectFromTable: () => {
    const ws = get().ws
    if (ws) {
      ws.close(1000, 'Player left')
    }
    set({
      ws: null,
      isConnected: false,
      tableId: null,
      gameState: null,
      isMyTurn: false,
      messages: [],
    })
  },

  sendAction: (action, amount) => {
    const ws = get().ws
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'ACTION', action, amount } as ClientMessage))
    }
  },

  sendChat: (text) => {
    const ws = get().ws
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'CHAT', text } as ClientMessage))
    }
  },

  sendEmote: (emoteId) => {
    const ws = get().ws
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'EMOTE', emoteId } as ClientMessage))
    }
  },

  sendPing: () => {
    const ws = get().ws
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'PING' } as ClientMessage))
    }
  },

  clearHandResult: () => {
    set({ lastHandResult: null, handAnimationActive: false })
  },

  reset: () => {
    set({
      ws: null,
      isConnected: false,
      tableId: null,
      gameState: null,
      isMyTurn: false,
      availableActions: [],
      minRaise: 0,
      maxRaise: 0,
      myPlayerId: null,
      lastHandResult: null,
      handAnimationActive: false,
      messages: [],
    })
  },
}))

// ── Server Message Handler ──────────────────────────────────────────────────
function handleServerMessage(
  msg: ServerMessage,
  set: (partial: Partial<GameStore> | ((state: GameStore) => Partial<GameStore>)) => void,
  get: () => GameStore,
): void {
  switch (msg.type) {
    case 'GAME_STATE':
      set({ gameState: msg.state, lastHandResult: null, handAnimationActive: false })
      break

    case 'YOUR_TURN':
      set({
        isMyTurn: true,
        availableActions: msg.actions,
        minRaise: msg.minRaise,
        maxRaise: msg.maxRaise,
      })
      break

    case 'PLAYER_ACTION': {
      const gs = get().gameState
      if (gs) {
        set({
          isMyTurn: false,
          gameState: {
            ...gs,
            players: gs.players.map(p =>
              p.id === msg.playerId
                ? { ...p, folded: msg.action === 'fold', isAllIn: msg.action === 'all_in' }
                : p,
            ),
            lastAction: { playerId: msg.playerId, type: msg.action, amount: msg.amount, timestamp: Date.now() },
          },
        })
      }
      break
    }

    case 'HAND_RESULT':
      set({ lastHandResult: msg.result, handAnimationActive: true })
      break

    case 'CHAT':
      set(state => ({
        messages: [
          ...state.messages.slice(-99),  // Keep last 100 messages
          {
            id: crypto.randomUUID(),
            playerId: msg.playerId,
            name: msg.name,
            text: msg.text,
            timestamp: Date.now(),
          },
        ],
      }))
      break

    case 'PLAYER_JOINED': {
      const gs = get().gameState
      if (gs) {
        set({
          gameState: {
            ...gs,
            players: [...gs.players, msg.player],
          },
        })
      }
      break
    }

    case 'PLAYER_LEFT': {
      const gs = get().gameState
      if (gs) {
        set({
          gameState: {
            ...gs,
            players: gs.players.filter(p => p.id !== msg.playerId),
          },
        })
      }
      break
    }

    case 'RECONNECT_STATE':
      set({
        gameState: msg.state,
        myPlayerId: msg.playerId,
      })
      break

    case 'ERROR':
      console.error('[Game] Server error:', msg.code, msg.message)
      break

    case 'PONG':
      break
  }
}
